import { faListUl, faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import Header from "../../components/header";
import ShareModal from "../../components/modal/ShareModal";
import { useTranslate } from "../../context/TranslationContext";
import { useGetReviewsQuery } from "../../redux/Apis/Review";
import { useGetInstructorsQuery } from "../../redux/Apis/User";
import usePageTheme from "../../hooks/usePageTheme";
import { createThemeVars, THEME_OPTIONS } from "../../utils/theme";
import "./style.css";

const InstructorReview = () => {
  const { t } = useTranslate("instructorDetails");
  const { user } = useSelector((state) => state.authSlice);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const { id } = useParams();
  const { data: instructorData } = useGetInstructorsQuery(
    { id, params: {} },
    { refetchOnMountOrArgChange: true, skip: !id },
  );
  const creatorTheme =
    instructorData?.data?.theme || instructorData?.data?.user?.theme;
  const { selectedTheme, setSelectedTheme, currentTheme, pageThemeVars } =
    usePageTheme({ defaultTheme: creatorTheme });
  const navigate = useNavigate();
  const themeVars = useMemo(
    () => createThemeVars(currentTheme),
    [currentTheme],
  );
  const isLightTheme = useMemo(
    () => currentTheme?.isLight === true,
    [currentTheme],
  );

  // ---------------- API ----------------
  const {
    data: reviewData,
    isLoading,
    isError,
  } = useGetReviewsQuery({
    params: { user: id },
  });

  //   ---------------- HANDLERS ----------------
  const user_image = useMemo(() => {
    if (!user?.picture) return images.profile.placeholder;
    if (user.picture.startsWith("http")) return user.picture;
    return user.picture.startsWith("images/")
      ? `${process.env.REACT_APP_S3_BASE_URL}${user.picture}`
      : images.profile.placeholder;
  }, [user?.picture]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("copySuccess"));
    } catch (error) {
      toast.error(t("copyFailed"));
    }
  };

  const handleSharePost = () => {
    const url = window.location.href;

    setShareUrl(url);
    setShowShareModal(true);
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("copySuccess"));
    } catch {
      toast.error(t("copyError"));
    }
  };

  const shareWhatsapp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareUrl)}`,
      "_blank",
    );
  };

  const shareTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`,
      "_blank",
    );
  };

  const shareLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareUrl,
      )}`,
      "_blank",
    );
  };

  const shareInstagram = async () => {
    await navigator.clipboard.writeText(shareUrl);

    window.location.href = "instagram://app";

    setTimeout(() => {
      window.open("https://www.instagram.com/", "_blank");
    }, 1000);
  };

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "_blank",
    );
  };

  // ---------------- UI ----------------
  return (
    <>
      <Helmet>
        <title>Creator's Reviews & Ratings | Traineego</title>
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://traineego.com/instructors" />
        <meta
          name="description"
          content="Read verified reviews, ratings, and learner feedback for this Creator on Traineego."
        />
      </Helmet>

      <Header
        showThemeToggle={true}
        themeOptions={THEME_OPTIONS}
        activeTheme={selectedTheme}
        onThemeChange={setSelectedTheme}
      />

      <div
        className={`min-vh-100 instructor-theme-page ${isLightTheme ? "instructor-theme-light" : ""}`}
        style={{
          padding: "150px 0 120px 0",
          ...pageThemeVars,
        }}
      >
        <h3 className="text-center mb-4">{t("reviews")}</h3>

        {/* LOADING */}
        {isLoading && (
          <p className="text-center">
            <Spinner />
          </p>
        )}

        {/* EMPTY */}
        {!isLoading && (!reviewData?.data?.length || isError) && (
          <p className="text-center text-muted">{t("noReviewFound")}</p>
        )}

        {/* LIST */}
        <div className="justify-content-center justify-items-center d-flex flex-wrap gap-3">
          <div className="col-lg-8">
            {reviewData?.data?.map((review) => {
              const fullName = `${review.user?.first_name || ""} ${
                review.user?.last_name || ""
              }`.trim();

              const initials = `${review.user?.first_name?.[0] || ""}${
                review.user?.last_name?.[0] || ""
              }`;

              const date = new Date(review?.createdAt);
              const formattedDate = `${String(date.getDate()).padStart(
                2,
                "0",
              )}-${String(date.getMonth() + 1).padStart(
                2,
                "0",
              )}-${date.getFullYear()}`;

              return (
                <div
                  key={review._id}
                  className="d-flex gap-3 p-3 mb-3 rounded shadow-sm"
                  style={{
                    background: isLightTheme ? "#fff" : "#0e0e18",
                    color: isLightTheme ? "#000" : "#fff",
                    border: "none",
                    boxShadow: `
                      inset 0 1px 0 rgba(255, 255, 255, 0.14),
                      0 8px 20px rgba(0, 0, 0, 0.28)
                    `,
                  }}
                >
                  {/* Avatar */}
                  {review.user?.picture ? (
                    <img
                      src={review.user.picture}
                      alt={fullName}
                      className="rounded-circle"
                      width="45"
                      height="45"
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 45,
                        height: 45,
                        background: "#2a2a40",
                        color: "#fff",
                      }}
                    >
                      {initials || "U"}
                    </div>
                  )}

                  {/* Content */}
                  <div className="w-100">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-0">{fullName || "Unknown User"}</h6>
                        <small className="text-muted">
                          {review.course?.title || "Unknown Course"}
                        </small>
                      </div>

                      {/* Rating */}
                      <div style={{ color: "#facc15" }}>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <span key={i}>{i <= review.rating ? "★" : "☆"}</span>
                        ))}
                      </div>
                    </div>

                    <p className="mt-2 mb-1">{review?.message}</p>

                    <small className="text-muted">{formattedDate}</small>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="sticky-action-wrapper" style={themeVars}>
        <div className="sticky-action-bar">
          <button className="action-btn" onClick={() => navigate("/")}>
            <i className="fas fa-home" />
          </button>

          <button className="action-btn" onClick={() => navigate("/community")}>
            <FontAwesomeIcon icon={faListUl} />
          </button>

          <button
            className={`action-btn ${showShareModal ? "active" : ""}`}
            onClick={() => handleSharePost()}
          >
            <FontAwesomeIcon icon={faShareNodes} />
          </button>

          {user && (
            <button
              className="action-btn profile-btn"
              onClick={() => {
                user?.role === "learner"
                  ? navigate("/learnerprofile", {
                      state: {
                        userProfileId: user?._id,
                        username: user?.username,
                      },
                    })
                  : navigate("/profile", {
                      state: {
                        userProfileId: user?._id,
                        username: user?.username,
                      },
                    });
              }}
            >
              <img src={user_image} alt="" className="profile-pic" />
            </button>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        t={t}
        shareWhatsapp={shareWhatsapp}
        shareInstagram={shareInstagram}
        shareFacebook={shareFacebook}
        shareLinkedIn={shareLinkedIn}
        shareTwitter={shareTwitter}
        handleCopyShareLink={handleCopyShareLink}
        themeVars={themeVars}
      />
    </>
  );
};

export default InstructorReview;
